import { FormEvent, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CalendarPlus2,
  CheckCircle2,
  ClipboardList,
  Loader2,
  RefreshCw,
  Search,
  Sparkles,
  Stethoscope,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import { useApiClient } from '../../lib/apiClient';
import { useI18n } from '../../i18n/LanguageContext';
import type { SupportedLanguage } from '../../i18n/translations';

type UrgentCareStatus = 'WAITING' | 'IN_TRIAGE' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
type UrgencyLevel = 'UNASSESSED' | 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';

type UrgentCareVisit = {
  visitId: string;
  patientId: string;
  patientMrn: string | null;
  patientName: string | null;
  chiefComplaint: string;
  symptoms: string[];
  notes: string | null;
  urgencyLevel: UrgencyLevel;
  urgencyScore: number;
  triageSummary: string | null;
  triageQuestions: string[];
  triageRedFlags: string[];
  triageNextSteps: string[];
  triageConfidence: number | null;
  triagedAt: string | null;
  status: UrgentCareStatus;
  arrivalAt: string | null;
  calledAt: string | null;
  completedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

type ProviderOption = {
  id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
};

type AppointmentTypeOption = {
  id: string;
  name: string;
  isTelemed?: boolean;
};

const urgencyMeta: Record<UrgencyLevel, { label: string; className: string }> = {
  UNASSESSED: { label: 'Unassessed', className: 'bg-slate-100 text-slate-700 border border-slate-200' },
  LOW: { label: 'Low', className: 'bg-emerald-50 text-emerald-700 border border-emerald-100' },
  MODERATE: { label: 'Moderate', className: 'bg-amber-50 text-amber-700 border border-amber-100' },
  HIGH: { label: 'High', className: 'bg-orange-50 text-orange-700 border border-orange-100' },
  CRITICAL: { label: 'Critical', className: 'bg-rose-50 text-rose-700 border border-rose-100' },
};

const statusMeta: Record<UrgentCareStatus, { label: string; className: string }> = {
  WAITING: { label: 'Waiting', className: 'bg-slate-50 text-slate-700 border border-slate-200' },
  IN_TRIAGE: { label: 'In triage', className: 'bg-sky-50 text-sky-700 border border-sky-100' },
  IN_PROGRESS: { label: 'In progress', className: 'bg-indigo-50 text-indigo-700 border border-indigo-100' },
  COMPLETED: { label: 'Completed', className: 'bg-emerald-50 text-emerald-700 border border-emerald-100' },
  CANCELLED: { label: 'Cancelled', className: 'bg-rose-50 text-rose-700 border border-rose-100' },
};

type PhraseTranslator = (phrase: string) => string;

const localeByLanguage: Record<SupportedLanguage, string> = {
  en: 'en-US',
  es: 'es-ES',
  it: 'it-IT',
};

const urgentCareEsOverrides: Record<string, string> = {
  'Walk-In Workspace': 'Espacio de ingresos',
  'Register arrivals, triage the queue, convert visits into patient appointments, and manage same-day intake.':
    'Registra llegadas, clasifica la cola, convierte visitas en citas de pacientes y gestiona ingresos del mismo dia.',
  'Refresh queue': 'Actualizar cola',
  'AI urgency triage': 'Triaje de urgencia con IA',
  'Register basic patient details, place arrivals into the waiting queue, and run AI urgency triage to sort patients by priority.':
    'Registra los datos basicos del paciente, coloca las llegadas en la cola de espera y ejecuta el triaje de urgencia con IA para ordenar a los pacientes por prioridad.',
  'Add Walk-In': 'Agregar ingreso',
  'First name': 'Nombre',
  'Last name': 'Apellido',
  Sex: 'Sexo',
  Female: 'Femenino',
  Male: 'Masculino',
  Other: 'Otro',
  Phone: 'Telefono',
  'MRN (optional)': 'MRN (opcional)',
  'Chief complaint': 'Motivo principal',
  'Symptoms (comma or new-line separated)': 'Sintomas (separados por comas o saltos de linea)',
  'Initial notes (optional)': 'Notas iniciales (opcional)',
  'Add to waiting list': 'Agregar a la lista de espera',
  'Waiting Queue': 'Cola de espera',
  'Search queue': 'Buscar en la cola',
  Patient: 'Paciente',
  Complaint: 'Motivo',
  Urgency: 'Urgencia',
  Status: 'Estado',
  Arrival: 'Llegada',
  'MRN unavailable': 'MRN no disponible',
  'Selected Visit Detail': 'Detalle de la visita seleccionada',
  'Re-run triage': 'Ejecutar triaje de nuevo',
  'Start visit': 'Iniciar visita',
  Complete: 'Completar',
  'Select a queue row to see triage details.': 'Selecciona una fila de la cola para ver los detalles del triaje.',
  Arrived: 'Llegada',
  'Urgency summary': 'Resumen de urgencia',
  'No triage summary yet. Run AI triage for this patient or queue.':
    'Aun no hay resumen de triaje. Ejecuta el triaje con IA para este paciente o para la cola.',
  Level: 'Nivel',
  Score: 'Puntuacion',
  Confidence: 'Confianza',
  'Red Flags': 'Senales de alerta',
  'Red flags': 'Senales de alerta',
  'Questions to ask now': 'Preguntas para hacer ahora',
  'Next steps generated from ai': 'Siguientes pasos generados por IA',
  'Suggested next steps': 'Siguientes pasos sugeridos',
  'Visit is': 'La visita esta',
  Unassessed: 'Sin evaluar',
  Low: 'Baja',
  Moderate: 'Moderada',
  High: 'Alta',
  Critical: 'Critica',
  Waiting: 'En espera',
  'In triage': 'En triaje',
  'In progress': 'En progreso',
  Completed: 'Completada',
  Cancelled: 'Cancelada',
  ACTIVE: 'Activas',
  ALL: 'Todas',
  IN_TRIAGE: 'En triaje',
  IN_PROGRESS: 'En progreso',
  COMPLETED: 'Completadas',
  CANCELLED: 'Canceladas',
  WAITING: 'En espera',
  'Failed to load urgent-care queue.': 'No se pudo cargar la cola de urgencias.',
  'First name, last name, date of birth, and chief complaint are required.':
    'Nombre, apellido, fecha de nacimiento y motivo principal son obligatorios.',
  'Patient registered and added to urgent-care waiting queue.':
    'Paciente registrado y agregado a la cola de espera de urgencias.',
  'Failed to register walk-in patient.': 'No se pudo registrar al paciente de ingreso.',
  'Failed to run queue triage.': 'No se pudo ejecutar el triaje de la cola.',
  'Patient triage refreshed.': 'Triaje del paciente actualizado.',
  'Failed to triage selected patient.': 'No se pudo clasificar al paciente seleccionado.',
  'Failed to update visit status.': 'No se pudo actualizar el estado de la visita.',
  'Search queue...': 'Buscar en la cola...',
  'No patients in the urgent-care queue for this filter.': 'No hay pacientes en la cola de urgencias para este filtro.',
  'Register Walk-In': 'Registrar ingreso',
  Close: 'Cerrar',
  'N/A': 'N/D',
  'Delete Visit': 'Eliminar visita',
  'Convert to Appointment': 'Convertir en cita',
  'Generate patient profile and appointment': 'Generar perfil de paciente y cita',
  'Choose provider and exact appointment window for this walk-in patient.':
    'Elige proveedor y ventana exacta de la cita para este paciente de ingreso.',
  'Delete walk-in visit?': 'Eliminar visita de ingreso?',
  'This removes the visit from the urgent-care queue.': 'Esto elimina la visita de la cola de urgencias.',
  'Convert walk-in': 'Convertir ingreso',
  Provider: 'Proveedor',
  'Appointment Type': 'Tipo de cita',
  'Start Time': 'Hora de inicio',
  'End Time': 'Hora de fin',
  'Visit deleted.': 'Visita eliminada.',
  'Failed to delete visit.': 'No se pudo eliminar la visita.',
  'Visit converted to appointment.': 'Visita convertida en cita.',
  'Failed to convert visit.': 'No se pudo convertir la visita.',
  'Select provider, appointment type, start time, and end time.': 'Selecciona proveedor, tipo de cita, hora de inicio y hora de fin.',
  'Open queue row for details and actions.': 'Abre una fila de la cola para ver detalles y acciones.',
};

const urgentCareItOverrides: Record<string, string> = {
  'Walk-In Workspace': 'Area accessi diretti',
  'Core Clinical': 'Clinica centrale',
  'Walk-ins': 'Accessi diretti',
  'Register arrivals, triage the queue, convert visits into patient appointments, and manage same-day intake.':
    'Registra gli arrivi, fai il triage della coda, converti le visite in appuntamenti paziente e gestisci l accettazione in giornata.',
  'Register basic patient details, place arrivals into the waiting queue, and run AI urgency triage to sort patients by priority.':
    'Registra i dati essenziali del paziente, inserisci gli arrivi nella coda di attesa ed esegui il triage IA per ordinare i pazienti per priorita.',
  'Refresh queue': 'Aggiorna coda',
  'AI urgency triage': 'Triage IA di urgenza',
  'Register Walk-In': 'Registra accesso diretto',
  'Add Walk-In': 'Aggiungi accesso diretto',
  'First name': 'Nome',
  'Last name': 'Cognome',
  Sex: 'Sesso',
  Female: 'Femmina',
  Male: 'Maschio',
  Other: 'Altro',
  Phone: 'Telefono',
  'MRN (optional)': 'MRN (opzionale)',
  'Chief complaint': 'Motivo principale',
  'Symptoms (comma or new-line separated)': 'Sintomi (separati da virgola o nuova riga)',
  'Initial notes (optional)': 'Note iniziali (opzionale)',
  'Add to waiting list': "Aggiungi alla lista d'attesa",
  'Waiting Queue': 'Coda di attesa',
  'Search queue': 'Cerca nella coda',
  'Search queue...': 'Cerca nella coda...',
  Patient: 'Paziente',
  Complaint: 'Motivo',
  Urgency: 'Urgenza',
  Status: 'Stato',
  Arrival: 'Arrivo',
  'MRN unavailable': 'MRN non disponibile',
  'No patients in the urgent-care queue for this filter.': 'Nessun paziente nella coda delle cure urgenti per questo filtro.',
  'Open queue row for details and actions.': 'Apri una riga della coda per vedere dettagli e azioni.',
  'Selected Visit Detail': 'Dettaglio visita selezionata',
  'Re-run triage': 'Esegui di nuovo il triage',
  'Start visit': 'Avvia visita',
  Complete: 'Completa',
  'Select a queue row to see triage details.': 'Seleziona una riga della coda per vedere i dettagli del triage.',
  Arrived: 'Arrivato',
  'Urgency summary': "Riepilogo dell'urgenza",
  'No triage summary yet. Run AI triage for this patient or queue.':
    'Nessun riepilogo del triage disponibile. Esegui il triage IA per questo paziente o per la coda.',
  Level: 'Livello',
  Score: 'Punteggio',
  Confidence: 'Affidabilita',
  'Red Flags': 'Segnali di allarme',
  'Red flags': 'Segnali di allarme',
  'Questions to ask now': 'Domande da porre ora',
  'Next steps generated from ai': 'Prossimi passi generati dall IA',
  'Suggested next steps': 'Prossimi passi suggeriti',
  'Visit is': 'La visita e',
  Unassessed: 'Non valutato',
  Low: 'Basso',
  Moderate: 'Moderato',
  High: 'Alto',
  Critical: 'Critico',
  Waiting: 'In attesa',
  'In triage': 'In triage',
  'In progress': 'In corso',
  Completed: 'Completata',
  Cancelled: 'Annullata',
  ACTIVE: 'Attivi',
  ALL: 'Tutti',
  IN_TRIAGE: 'In triage',
  IN_PROGRESS: 'In corso',
  COMPLETED: 'Completate',
  CANCELLED: 'Annullate',
  WAITING: 'In attesa',
  'Failed to load urgent-care queue.': 'Impossibile caricare la coda delle cure urgenti.',
  'First name, last name, date of birth, and chief complaint are required.':
    'Nome, cognome, data di nascita e motivo principale sono obbligatori.',
  'Patient registered and added to urgent-care waiting queue.':
    'Paziente registrato e aggiunto alla coda di attesa delle cure urgenti.',
  'Failed to register walk-in patient.': "Impossibile registrare il paziente in accesso diretto.",
  'Failed to run queue triage.': 'Impossibile eseguire il triage della coda.',
  'Patient triage refreshed.': 'Triage del paziente aggiornato.',
  'Failed to triage selected patient.': 'Impossibile eseguire il triage del paziente selezionato.',
  'Failed to update visit status.': 'Impossibile aggiornare lo stato della visita.',
  'Delete Visit': 'Elimina visita',
  'Convert to Appointment': 'Converti in appuntamento',
  'Generate patient profile and appointment': 'Genera profilo paziente e appuntamento',
  'Choose provider and exact appointment window for this walk-in patient.':
    'Scegli il medico e la finestra oraria esatta dell appuntamento per questo paziente in accesso diretto.',
  'Delete walk-in visit?': 'Eliminare la visita in accesso diretto?',
  'This removes the visit from the urgent-care queue.': 'Questo rimuove la visita dalla coda delle cure urgenti.',
  'Convert walk-in': 'Converti accesso diretto',
  Provider: 'Medico',
  'Appointment Type': 'Tipo di appuntamento',
  'Start Time': 'Ora di inizio',
  'End Time': 'Ora di fine',
  'Visit deleted.': 'Visita eliminata.',
  'Failed to delete visit.': 'Impossibile eliminare la visita.',
  'Visit converted to appointment.': 'Visita convertita in appuntamento.',
  'Failed to convert visit.': 'Impossibile convertire la visita.',
  'Select provider, appointment type, start time, and end time.':
    'Seleziona medico, tipo di appuntamento, ora di inizio e ora di fine.',
  Close: 'Chiudi',
  'N/A': 'N/D',
};

function getLocale(language: SupportedLanguage) {
  return localeByLanguage[language] ?? 'en-US';
}

function createUrgentCareTranslator(language: SupportedLanguage, tr: PhraseTranslator): PhraseTranslator {
  if (language === 'en') return tr;

  return (phrase: string) => {
    const normalized = phrase.trim();
    if (!normalized) return phrase;
    const globalTranslation = tr(normalized);
    if (globalTranslation !== normalized) {
      return phrase.replace(normalized, globalTranslation);
    }
    if (language === 'es' || language === 'it') {
      const overrides = language === 'es' ? urgentCareEsOverrides : urgentCareItOverrides;
      const direct = overrides[normalized];
      if (direct) return phrase.replace(normalized, direct);
    }
    return phrase;
  };
}

function formatDateTime(value: string | null | undefined, locale: string, translate: PhraseTranslator) {
  if (!value) return translate('N/A');
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return translate('N/A');
  return parsed.toLocaleString(locale);
}

function parseSymptomsText(value: string) {
  return value
    .split(/\n|,/g)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 30);
}

function getPatientInitials(value: string | null | undefined, fallback = 'PT') {
  const normalized = String(value || '').trim();
  if (!normalized) return fallback;
  return (
    normalized
      .split(/\s+/)
      .filter(Boolean)
      .map((segment) => segment[0] || '')
      .join('')
      .slice(0, 2)
      .toUpperCase() || fallback
  );
}

function toLocalDateTimeInput(value: Date) {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  const hour = `${value.getHours()}`.padStart(2, '0');
  const minute = `${value.getMinutes()}`.padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function defaultWindowForVisit(visit: UrgentCareVisit | null) {
  const base = visit?.arrivalAt ? new Date(visit.arrivalAt) : new Date();
  if (Number.isNaN(base.getTime())) {
    const now = new Date();
    return {
      start: toLocalDateTimeInput(now),
      end: toLocalDateTimeInput(new Date(now.getTime() + 30 * 60_000)),
    };
  }
  const start = new Date(base);
  const end = new Date(base.getTime() + 30 * 60_000);
  return {
    start: toLocalDateTimeInput(start),
    end: toLocalDateTimeInput(end),
  };
}

export default function WalkInUrgentCarePage() {
  const { language, tr } = useI18n();
  const locale = getLocale(language);
  const tu = useMemo(() => createUrgentCareTranslator(language, tr), [language, tr]);
  const { fetchJson } = useApiClient();

  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string>('');
  const [queueSearch, setQueueSearch] = useState('');
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [deleteDialogVisit, setDeleteDialogVisit] = useState<UrgentCareVisit | null>(null);
  const [convertDialogVisit, setConvertDialogVisit] = useState<UrgentCareVisit | null>(null);
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentTypeOption[]>([]);

  const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'ALL' | UrgentCareStatus>('ACTIVE');
  const [visits, setVisits] = useState<UrgentCareVisit[]>([]);
  const [expandedVisitId, setExpandedVisitId] = useState<string>('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [sex, setSex] = useState('');
  const [phone, setPhone] = useState('');
  const [mrn, setMrn] = useState('');
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [symptomsText, setSymptomsText] = useState('');
  const [notes, setNotes] = useState('');

  const [convertProviderId, setConvertProviderId] = useState('');
  const [convertAppointmentTypeId, setConvertAppointmentTypeId] = useState('');
  const [convertStartTime, setConvertStartTime] = useState('');
  const [convertEndTime, setConvertEndTime] = useState('');

  const filteredVisits = useMemo(() => {
    const q = queueSearch.trim().toLowerCase();
    if (!q) return visits;
    return visits.filter((visit) =>
      [visit.patientName, visit.patientMrn, visit.chiefComplaint, visit.status, visit.urgencyLevel].join(' ').toLowerCase().includes(q),
    );
  }, [queueSearch, visits]);

  const nonTelemedAppointmentTypes = useMemo(
    () => appointmentTypes.filter((type) => !type.isTelemed),
    [appointmentTypes],
  );

  const loadVisits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams();
      if (statusFilter === 'ACTIVE') qs.set('activeOnly', 'true');
      else if (statusFilter !== 'ALL') qs.set('status', statusFilter);
      const response = await fetchJson<{ data: UrgentCareVisit[] }>(`/urgent-care/walk-ins?${qs.toString()}`);
      const rows = response.data || [];
      setVisits(rows);
      setExpandedVisitId((current) => (current && rows.some((item) => item.visitId === current) ? current : ''));
    } catch (err: any) {
      setError(tu(err?.message || 'Failed to load urgent-care queue.'));
    } finally {
      setLoading(false);
    }
  }, [fetchJson, statusFilter, tu]);

  const loadReferenceData = useCallback(async () => {
    try {
      const [providerRows, typeRows] = await Promise.all([
        fetchJson<ProviderOption[]>('/providers'),
        fetchJson<AppointmentTypeOption[]>('/appointment-types'),
      ]);
      setProviders(Array.isArray(providerRows) ? providerRows : []);
      setAppointmentTypes(Array.isArray(typeRows) ? typeRows : []);
    } catch {
      setProviders([]);
      setAppointmentTypes([]);
    }
  }, [fetchJson]);

  useEffect(() => {
    void loadVisits();
  }, [loadVisits]);

  useEffect(() => {
    void loadReferenceData();
  }, [loadReferenceData]);

  function openRegisterDialog() {
    setError(null);
    setNotice('');
    setRegisterDialogOpen(true);
  }

  function openConvertDialog(visit: UrgentCareVisit) {
    const window = defaultWindowForVisit(visit);
    setConvertDialogVisit(visit);
    setConvertProviderId(providers[0]?.id ?? '');
    setConvertAppointmentTypeId(nonTelemedAppointmentTypes[0]?.id ?? appointmentTypes[0]?.id ?? '');
    setConvertStartTime(window.start);
    setConvertEndTime(window.end);
  }

  async function handleRegisterWalkIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice('');

    if (!firstName.trim() || !lastName.trim() || !dob || !chiefComplaint.trim()) {
      setError(tu('First name, last name, date of birth, and chief complaint are required.'));
      return;
    }

    setWorking(true);
    try {
      const payload = {
        patient: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dob: new Date(`${dob}T00:00:00.000Z`).toISOString(),
          sex: sex.trim() || undefined,
          phone: phone.trim() || undefined,
          mrn: mrn.trim() || undefined,
        },
        chiefComplaint: chiefComplaint.trim(),
        symptoms: parseSymptomsText(symptomsText),
        notes: notes.trim() || undefined,
      };
      const response = await fetchJson<{ data: UrgentCareVisit }>('/urgent-care/walk-ins', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setNotice(tu('Patient registered and added to urgent-care waiting queue.'));
      setExpandedVisitId(response.data.visitId);
      setFirstName('');
      setLastName('');
      setDob('');
      setSex('');
      setPhone('');
      setMrn('');
      setChiefComplaint('');
      setSymptomsText('');
      setNotes('');
      setRegisterDialogOpen(false);
      await loadVisits();
    } catch (err: any) {
      setError(tu(err?.message || 'Failed to register walk-in patient.'));
    } finally {
      setWorking(false);
    }
  }

  async function handleRunQueueTriage() {
    setError(null);
    setNotice('');
    setWorking(true);
    try {
      const response = await fetchJson<{ data: { processed: number; queue: UrgentCareVisit[] } }>('/urgent-care/triage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locale: language }),
      });
      setVisits(response.data.queue || []);
      setNotice(
        language === 'es'
          ? `El triaje IA proceso ${response.data.processed} paciente(s). La cola se reordeno por urgencia.`
          : language === 'it'
          ? `Il triage IA ha elaborato ${response.data.processed} paziente/i. Coda riordinata per urgenza.`
          : `AI triage processed ${response.data.processed} patient(s). Queue re-sorted by urgency.`,
      );
    } catch (err: any) {
      setError(tu(err?.message || 'Failed to run queue triage.'));
    } finally {
      setWorking(false);
    }
  }

  async function handleTriageOne(visitId: string) {
    setError(null);
    setNotice('');
    setWorking(true);
    try {
      await fetchJson<{ data: UrgentCareVisit }>(`/urgent-care/walk-ins/${visitId}/triage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: true, locale: language }),
      });
      setNotice(tu('Patient triage refreshed.'));
      await loadVisits();
    } catch (err: any) {
      setError(tu(err?.message || 'Failed to triage selected patient.'));
    } finally {
      setWorking(false);
    }
  }

  async function handleStatusChange(visitId: string, status: UrgentCareStatus) {
    setError(null);
    setNotice('');
    setWorking(true);
    try {
      await fetchJson<{ data: UrgentCareVisit }>(`/urgent-care/walk-ins/${visitId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      setNotice(
        language === 'es'
          ? `Visita marcada como ${tu(statusMeta[status].label)}.`
          : `Visit marked as ${statusMeta[status].label}.`,
      );
      await loadVisits();
    } catch (err: any) {
      setError(tu(err?.message || 'Failed to update visit status.'));
    } finally {
      setWorking(false);
    }
  }

  async function handleDeleteVisit() {
    if (!deleteDialogVisit) return;
    setWorking(true);
    setError(null);
    setNotice('');
    try {
      await fetchJson(`/urgent-care/walk-ins/${deleteDialogVisit.visitId}`, { method: 'DELETE' });
      setDeleteDialogVisit(null);
      setExpandedVisitId('');
      setNotice(tu('Visit deleted.'));
      await loadVisits();
    } catch (err: any) {
      setError(tu(err?.message || 'Failed to delete visit.'));
    } finally {
      setWorking(false);
    }
  }

  async function handleConvertVisit() {
    if (!convertDialogVisit) return;
    if (!convertProviderId || !convertAppointmentTypeId || !convertStartTime || !convertEndTime) {
      setError(tu('Select provider, appointment type, start time, and end time.'));
      return;
    }
    setWorking(true);
    setError(null);
    setNotice('');
    try {
      await fetchJson(`/urgent-care/walk-ins/${convertDialogVisit.visitId}/convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          providerId: convertProviderId,
          appointmentTypeId: convertAppointmentTypeId,
          startTime: new Date(convertStartTime).toISOString(),
          endTime: new Date(convertEndTime).toISOString(),
        }),
      });
      setConvertDialogVisit(null);
      setNotice(tu('Visit converted to appointment.'));
      await loadVisits();
    } catch (err: any) {
      setError(tu(err?.message || 'Failed to convert visit.'));
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lime-700">{tu('Walk-In Workspace')}</p>
            <h1 className="mt-1 text-2xl font-semibold text-ink">{tu('Walk-ins')}</h1>
            <p className="max-w-3xl text-sm text-slate-600">
              {tu('Register arrivals, triage the queue, convert visits into patient appointments, and manage same-day intake.')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={loadVisits}
              disabled={loading || working}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              <RefreshCw size={16} />
              {tu('Refresh queue')}
            </button>
            <button
              onClick={handleRunQueueTriage}
              disabled={working}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              {working ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {tu('AI urgency triage')}
            </button>
            <button
              onClick={openRegisterDialog}
              disabled={working}
              className="inline-flex items-center gap-2 rounded-xl bg-lime-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-lime-300 disabled:opacity-60"
            >
              <UserPlus size={16} />
              {tu('Add Walk-In')}
            </button>
          </div>
        </div>
      </div>

      {error ? <div className="rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
      {notice ? <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{notice}</div> : null}

      <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{tu('Waiting Queue')}</h2>
            <p className="text-sm text-slate-500">{tu('Open queue row for details and actions.')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-full min-w-[240px] max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={queueSearch}
                onChange={(event) => setQueueSearch(event.target.value)}
                placeholder={tu('Search queue...')}
                aria-label={tu('Search queue')}
                className="w-full rounded-xl border border-slate-300 px-3 py-2.5 pl-9 text-sm outline-none focus:border-lime-400 focus:ring-2 focus:ring-lime-100"
              />
            </div>
            <div className="flex flex-wrap gap-1">
              {(['ACTIVE', 'ALL', 'WAITING', 'IN_TRIAGE', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`rounded-full border px-2.5 py-1 text-xs ${
                    statusFilter === status ? 'border-ink bg-ink text-white' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {tu(status)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3 font-medium">{tu('Patient')}</th>
                <th className="px-4 py-3 font-medium">{tu('Complaint')}</th>
                <th className="px-4 py-3 font-medium">{tu('Urgency')}</th>
                <th className="px-4 py-3 font-medium">{tu('Status')}</th>
                <th className="px-4 py-3 font-medium">{tu('Arrival')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredVisits.map((visit) => {
                const active = visit.visitId === expandedVisitId;
                return (
                  <Fragment key={visit.visitId}>
                    <tr
                      onClick={() => setExpandedVisitId((current) => (current === visit.visitId ? '' : visit.visitId))}
                      className={`cursor-pointer ${active ? 'bg-slate-50' : 'hover:bg-slate-50'}`}
                    >
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-100 text-xs font-semibold text-slate-600">
                            {getPatientInitials(visit.patientName || visit.patientId)}
                          </span>
                          <div className="min-w-0">
                            <div className="truncate font-medium text-slate-800">{visit.patientName || visit.patientId}</div>
                            <div className="text-xs text-slate-500">{visit.patientMrn || tu('MRN unavailable')}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 align-top text-slate-700">{visit.chiefComplaint}</td>
                      <td className="px-4 py-3 align-top">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${urgencyMeta[visit.urgencyLevel].className}`}>
                          {tu(urgencyMeta[visit.urgencyLevel].label)} ({visit.urgencyScore.toLocaleString(locale)})
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusMeta[visit.status].className}`}>
                          {tu(statusMeta[visit.status].label)}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-xs text-slate-600">{formatDateTime(visit.arrivalAt, locale, tu)}</td>
                    </tr>
                    {active ? (
                      <tr className="bg-slate-50/70">
                        <td colSpan={5} className="px-4 py-4">
                          <div className="space-y-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <h3 className="text-sm font-semibold text-slate-900">{visit.patientName || visit.patientId}</h3>
                                <p className="mt-1 text-xs text-slate-500">
                                  {visit.patientMrn || tu('MRN unavailable')} • {tu('Arrived')} {formatDateTime(visit.arrivalAt, locale, tu)}
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  onClick={() => handleTriageOne(visit.visitId)}
                                  disabled={working}
                                  className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                                >
                                  <Sparkles size={13} />
                                  {tu('Re-run triage')}
                                </button>
                                <button
                                  onClick={() => openConvertDialog(visit)}
                                  disabled={working}
                                  className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                                >
                                  <CalendarPlus2 size={13} />
                                  {tu('Convert to Appointment')}
                                </button>
                                <button
                                  onClick={() => setDeleteDialogVisit(visit)}
                                  disabled={working}
                                  className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-60"
                                >
                                  <Trash2 size={13} />
                                  {tu('Delete Visit')}
                                </button>
                                <button
                                  onClick={() => handleStatusChange(visit.visitId, 'IN_PROGRESS')}
                                  disabled={working}
                                  className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                                >
                                  {tu('Start visit')}
                                </button>
                                <button
                                  onClick={() => handleStatusChange(visit.visitId, 'COMPLETED')}
                                  disabled={working}
                                  className="inline-flex items-center gap-1 rounded-lg bg-lime-400 px-3 py-1.5 text-xs font-semibold text-slate-900 hover:bg-lime-300 disabled:opacity-60"
                                >
                                  {tu('Complete')}
                                </button>
                              </div>
                            </div>

                            <div className="grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
                              <div className="space-y-3">
                                <div className="rounded-xl border border-slate-200 bg-white p-3">
                                  <p className="text-sm text-slate-700">{visit.chiefComplaint}</p>
                                  {visit.notes ? <p className="mt-2 text-sm text-slate-500">{visit.notes}</p> : null}
                                </div>
                                <div className="rounded-xl border border-slate-200 bg-white p-3">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{tu('Urgency summary')}</p>
                                  <p className="mt-1 text-sm text-slate-700">
                                    {visit.triageSummary ? tu(visit.triageSummary) : tu('No triage summary yet. Run AI triage for this patient or queue.')}
                                  </p>
                                  <p className="mt-2 text-xs text-slate-500">
                                    {tu('Level')}: {tu(urgencyMeta[visit.urgencyLevel].label)} • {tu('Score')}: {visit.urgencyScore.toLocaleString(locale)}
                                    {visit.triageConfidence !== null
                                      ? ` • ${tu('Confidence')} ${Math.round(visit.triageConfidence * 100).toLocaleString(locale)}%`
                                      : ''}
                                  </p>
                                </div>
                              </div>

                              <div className="space-y-3">
                                {visit.triageRedFlags.length > 0 ? (
                                  <div>
                                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-rose-600">{tu('Red flags')}</p>
                                    <ul className="space-y-1">
                                      {visit.triageRedFlags.map((item, index) => (
                                        <li key={`${item}-${index}`} className="flex items-start gap-2 text-sm text-rose-700">
                                          <AlertTriangle size={14} className="mt-0.5" />
                                          <span>{tu(item)}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null}
                                {visit.triageQuestions.length > 0 ? (
                                  <div>
                                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">{tu('Questions to ask now')}</p>
                                    <ul className="space-y-1">
                                      {visit.triageQuestions.map((item, index) => (
                                        <li key={`${item}-${index}`} className="flex items-start gap-2 text-sm text-slate-700">
                                          <Stethoscope size={14} className="mt-0.5 text-slate-500" />
                                          <span>{tu(item)}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null}
                                {visit.triageNextSteps.length > 0 ? (
                                  <div>
                                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">{tu('Suggested next steps')}</p>
                                    <ul className="space-y-1">
                                      {visit.triageNextSteps.map((item, index) => (
                                        <li key={`${item}-${index}`} className="flex items-start gap-2 text-sm text-slate-700">
                                          <ClipboardList size={14} className="mt-0.5 text-slate-500" />
                                          <span>{tu(item)}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ) : null}
                                {(visit.status === 'COMPLETED' || visit.status === 'CANCELLED') ? (
                                  <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-sm text-emerald-700">
                                    <CheckCircle2 size={14} className="mr-2 inline-block" />
                                    {tu('Visit is')} {tu(statusMeta[visit.status].label).toLowerCase()}.
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {!filteredVisits.length ? (
            <p className="px-4 py-6 text-sm text-slate-500">
              {loading ? tu('Loading...') : tu('No patients in the urgent-care queue for this filter.')}
            </p>
          ) : null}
        </div>
      </section>

      {registerDialogOpen ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/40 p-4" onClick={() => !working && setRegisterDialogOpen(false)}>
          <div className="mt-10 w-full max-w-2xl rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lime-700">{tu('Register Walk-In')}</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">{tu('Add Walk-In')}</h2>
              </div>
              <button
                onClick={() => setRegisterDialogOpen(false)}
                disabled={working}
                className="rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                aria-label={tu('Close')}
              >
                <X size={16} />
              </button>
            </div>
            <form className="mt-4 space-y-3" onSubmit={handleRegisterWalkIn}>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <input className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm" placeholder={tu('First name')} value={firstName} onChange={(event) => setFirstName(event.target.value)} />
                <input className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm" placeholder={tu('Last name')} value={lastName} onChange={(event) => setLastName(event.target.value)} />
                <input className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm" type="date" value={dob} onChange={(event) => setDob(event.target.value)} />
                <select className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm" value={sex} onChange={(event) => setSex(event.target.value)}>
                  <option value="">{tu('Sex')}</option>
                  <option value="Female">{tu('Female')}</option>
                  <option value="Male">{tu('Male')}</option>
                  <option value="Other">{tu('Other')}</option>
                </select>
                <input className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm" placeholder={tu('Phone')} value={phone} onChange={(event) => setPhone(event.target.value)} />
                <input className="rounded-xl border border-slate-300 px-3 py-2.5 text-sm" placeholder={tu('MRN (optional)')} value={mrn} onChange={(event) => setMrn(event.target.value)} />
              </div>
              <input className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" placeholder={tu('Chief complaint')} value={chiefComplaint} onChange={(event) => setChiefComplaint(event.target.value)} />
              <textarea className="min-h-[90px] w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" placeholder={tu('Symptoms (comma or new-line separated)')} value={symptomsText} onChange={(event) => setSymptomsText(event.target.value)} />
              <textarea className="min-h-[90px] w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm" placeholder={tu('Initial notes (optional)')} value={notes} onChange={(event) => setNotes(event.target.value)} />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setRegisterDialogOpen(false)} disabled={working} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  {tu('Cancel')}
                </button>
                <button type="submit" disabled={working} className="rounded-xl bg-lime-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-lime-300 disabled:opacity-60">
                  {working ? <Loader2 size={16} className="mr-2 inline-block animate-spin" /> : <UserPlus size={16} className="mr-2 inline-block" />}
                  {tu('Add Walk-In')}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {deleteDialogVisit ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/40 p-4" onClick={() => !working && setDeleteDialogVisit(null)}>
          <div className="mt-20 w-full max-w-md rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-600">{tu('Delete Visit')}</p>
            <h2 className="mt-1 text-xl font-semibold text-slate-900">{tu('Delete walk-in visit?')}</h2>
            <p className="mt-2 text-sm text-slate-500">{tu('This removes the visit from the urgent-care queue.')}</p>
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <div className="font-medium">{deleteDialogVisit.patientName || deleteDialogVisit.patientId}</div>
              <div className="text-xs text-slate-500">{deleteDialogVisit.chiefComplaint}</div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteDialogVisit(null)} disabled={working} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                {tu('Cancel')}
              </button>
              <button type="button" onClick={() => void handleDeleteVisit()} disabled={working} className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60">
                {working ? <Loader2 size={16} className="mr-2 inline-block animate-spin" /> : <Trash2 size={16} className="mr-2 inline-block" />}
                {tu('Delete')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {convertDialogVisit ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/40 p-4" onClick={() => !working && setConvertDialogVisit(null)}>
          <div className="mt-10 w-full max-w-2xl rounded-3xl border border-slate-200 bg-white p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lime-700">{tu('Convert walk-in')}</p>
                <h2 className="mt-1 text-xl font-semibold text-slate-900">{tu('Generate patient profile and appointment')}</h2>
                <p className="text-sm text-slate-500">{tu('Choose provider and exact appointment window for this walk-in patient.')}</p>
              </div>
              <button type="button" onClick={() => setConvertDialogVisit(null)} disabled={working} className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                <X size={16} />
              </button>
            </div>
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <div className="font-medium">{convertDialogVisit.patientName || convertDialogVisit.patientId}</div>
              <div className="text-xs text-slate-500">{convertDialogVisit.chiefComplaint}</div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <label className="text-sm text-slate-600">
                {tu('Provider')}
                <select value={convertProviderId} onChange={(event) => setConvertProviderId(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white">
                  <option value="">{tu('Provider')}</option>
                  {providers.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name || `${provider.firstName || ''} ${provider.lastName || ''}`.trim() || provider.id}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm text-slate-600">
                {tu('Appointment Type')}
                <select value={convertAppointmentTypeId} onChange={(event) => setConvertAppointmentTypeId(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white">
                  <option value="">{tu('Appointment Type')}</option>
                  {nonTelemedAppointmentTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm text-slate-600">
                {tu('Start Time')}
                <input type="datetime-local" value={convertStartTime} onChange={(event) => setConvertStartTime(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white" />
              </label>
              <label className="text-sm text-slate-600">
                {tu('End Time')}
                <input type="datetime-local" value={convertEndTime} onChange={(event) => setConvertEndTime(event.target.value)} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 bg-white" />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setConvertDialogVisit(null)} disabled={working} className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                {tu('Cancel')}
              </button>
              <button type="button" onClick={() => void handleConvertVisit()} disabled={working} className="rounded-xl bg-lime-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-lime-300 disabled:opacity-60">
                {working ? <Loader2 size={16} className="mr-2 inline-block animate-spin" /> : <CalendarPlus2 size={16} className="mr-2 inline-block" />}
                {tu('Convert to Appointment')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
